import { useState, useRef, useMemo } from "react";
import useAppContext from "../components/AppContext";
import { getCookieValue } from "./cookies";
import { useEnroll } from "./sms";
import { useCreateSequenceUser, useSequenceRedirectUrl } from "./sequence";
import {
  SUCCESS,
  COMPOUNDED_SEMAGLUTIDE_ITEM,
  COMPOUNDED_SEMAGLUTIDE_USAGE_BASED_ADDON,
  COMPOUNDED_SEMAGLUTIDE_ITEM_V2
} from "../components/ClinicCheckout/constants";
import t from "./translate";
import { WWFTM } from "components/Results/constants";
import { updateAssessmentId } from "./account";
import useUrlSlugContext from "components/UrlSlugContext";
import getAidFromCookie from "./getAidFromCookie";
import { logger } from "./logger";

/**
 * @typedef Data
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} street1
 * @property {string} street2
 * @property {string} city
 * @property {string} state
 * @property {string} postalCode
 * @property {string} phone
 * @property {string} country
 * @property {string} token
 */

/**
 *
 * @param {object} props
 * @param {string} props.planId
 * @returns
 */
const useCreateClinicSub = () => {
  const { baseUrl } = useUrlSlugContext();
  const { contact, newQuiz, authData, setAuthData } = useAppContext();

  const [tokenIsError, setTokenIsError] = useState(!authData?.loggedInAs);

  const sequenceUserCreated = useRef(false);
  const aid = getAidFromCookie(newQuiz, baseUrl);

  const { market } = contact;
  const {
    trigger: enroll,
    error: enrollError,
    isError: enrollIsError,
    isMutating: isEnrolling
  } = useEnroll();
  const {
    trigger: createSequenceUser,
    error: createSequenceUserError,
    isError: createSequenceUserIsError,
    isMutating: isCreatingSequenceUser
  } = useCreateSequenceUser();
  const {
    trigger: getSequenceRedirectUrl,
    error: getSequenceRedirectUrlError,
    isError: getSequenceRedirectUrlIsError,
    isMutating: isGettingSequenceRedirectUrl
  } = useSequenceRedirectUrl();

  const isLoading =
    isEnrolling || isCreatingSequenceUser || isGettingSequenceRedirectUrl;
  const error =
    enrollError || createSequenceUserError || getSequenceRedirectUrlError;
  const isError =
    enrollIsError ||
    createSequenceUserIsError ||
    getSequenceRedirectUrlIsError ||
    tokenIsError;
  const errors = {
    tokenIsError,
    enrollError,
    createSequenceUserIsError,
    getSequenceRedirectUrlIsError
  };

  const errorMessaging = useMemo(() => {
    const {
      tokenIsError,
      createSequenceUserIsError,
      getSequenceRedirectUrlIsError
    } = errors || {};
    if (tokenIsError) {
      return t("LOGIN_REQUIRED_ERROR");
    } else if (createSequenceUserIsError || getSequenceRedirectUrlIsError) {
      return t("SEQUENCE_CREATION_ERROR");
    } else if (enrollError) {
      return enrollError;
    }

    return t("CLINIC_CHECKOUT_ERROR");
  }, [errors]);

  /**
   *
   * @param {{
   * data: Data
   * }} param0
   */
  const create = async ({ data, plan }) => {
    try {
      const token = getCookieValue("wwSession");

      if (!token) {
        setTokenIsError(true);
        throw new Error("Login required");
      }

      if (aid) {
        await updateAssessmentId({
          seqAssessmentId: aid,
          market
        });
      } else {
        logger("Error: no aid found", {
          level: "error"
        });
      }

      const payload = getEnrollPayload(data, plan);
      const res = await enroll({ market, payload, authData, setAuthData });
      const enrollmentData = res.data;
      return enrollmentData;
    } catch (error) {
      logger("Error creating clinic subscription", {
        level: "error",
        customAttributes: { error }
      });
    }
  };

  const getRedirectUrl = async enrollmentData => {
    try {
      const { ww } = enrollmentData;
      const { sequenceUserCreationStatus } = ww;
      const shouldCreateSequenceUser =
        sequenceUserCreationStatus !== SUCCESS && !sequenceUserCreated.current;

      if (shouldCreateSequenceUser) {
        await createSequenceUser({ market });
        sequenceUserCreated.current = true;
      }

      const res = await getSequenceRedirectUrl();

      logger("Successfully got sequence redirect URL", {
        level: "info"
      });

      const { redirect } = res.data;
      return redirect;
    } catch (error) {
      logger("Error getting sequence redirect URL", {
        level: "error",
        customAttributes: { error }
      });
    }
  };

  const createIneligibleUser = async seqAssessmentId => {
    try {
      const token = getCookieValue("wwSession");

      if (!token || !seqAssessmentId) return;

      await createSequenceUser({ market });

      logger("Successfully created ineligible sequence user", {
        level: "info"
      });
    } catch (error) {
      logger("Error creating ineligible sequence user", {
        level: "error",
        customAttributes: { error }
      });
    }
  };

  return {
    isLoading,
    isError,
    error,
    errorMessaging,
    errors,
    create,
    createIneligibleUser,
    getRedirectUrl
  };
};

export default useCreateClinicSub;

/**
 *
 * @param {Data} data
 */
function getEnrollPayload(data, plan) {
  const { token, couponCode } = data;
  const coachReferralToken = getCookieValue(WWFTM);
  let compoundAddon = false;

  if (plan?.addons?.length) {
    compoundAddon = plan.addons.find(addon =>
      [COMPOUNDED_SEMAGLUTIDE_ITEM, COMPOUNDED_SEMAGLUTIDE_ITEM_V2].includes(
        addon?.addonItem?.baseItem?.code
      )
    );
  }

  return {
    billingInfo: {
      tokenId: token
    },
    offerPlanId: plan.id,
    ...(coachReferralToken && { coachReferralToken }),
    ...(couponCode
      ? {
          couponCodes: [couponCode]
        }
      : {}),
    ...(compoundAddon
      ? {
          addOns: [
            {
              code: COMPOUNDED_SEMAGLUTIDE_USAGE_BASED_ADDON
            }
          ]
        }
      : {})
  };
}
